const arbitraryColor = /^(bg|text|border|from|via|to)-\[#([0-9a-f]{3,8})\](?:\/(\d{1,3}))?$/i;
const namedGradientColor = /^(from|via|to)-([a-z]+-\d{2,3})(?:\/(\d{1,3}))?$/i;
const gradientPalette: Readonly<Record<string, string>> = {
  "slate-50": "f8fafc", "slate-950": "020617",
  "red-50": "fef2f2", "red-800": "991b1b", "red-900": "7f1d1d", "red-950": "450a0a",
  "orange-50": "fff7ed", "orange-500": "f97316", "orange-600": "ea580c",
  "amber-50": "fffbeb", "amber-400": "fbbf24", "amber-500": "f59e0b",
  "yellow-50": "fefce8", "lime-500": "84cc16",
  "emerald-50": "ecfdf5", "emerald-600": "059669", "emerald-700": "047857", "emerald-800": "065f46",
  "teal-50": "f0fdfa",
  "cyan-50": "ecfeff", "cyan-500": "06b6d4", "cyan-700": "0e7490", "cyan-800": "155e75", "cyan-950": "083344",
  "sky-50": "f0f9ff", "blue-50": "eff6ff", "blue-700": "1d4ed8",
  "indigo-50": "eef2ff", "indigo-700": "4338ca", "indigo-800": "3730a3", "indigo-950": "1e1b4b",
  "violet-50": "f5f3ff", "violet-700": "6d28d9", "violet-800": "5b21b6", "violet-950": "2e1065",
  "fuchsia-50": "fdf4ff", "fuchsia-600": "c026d3", "fuchsia-700": "a21caf", "fuchsia-950": "4a044e",
  "pink-50": "fdf2f8", "rose-50": "fff1f2", "rose-600": "e11d48", "rose-700": "be123c", "rose-950": "4c0519",
};
const directions: Readonly<Record<string, string>> = {
  "bg-gradient-to-r": "to right",
  "bg-gradient-to-l": "to left",
  "bg-gradient-to-b": "to bottom",
  "bg-gradient-to-t": "to top",
  "bg-gradient-to-br": "to bottom right",
  "bg-gradient-to-bl": "to bottom left",
  "bg-gradient-to-tr": "to top right",
  "bg-gradient-to-tl": "to top left",
};

function expandHex(value: string) {
  if (value.length === 3 || value.length === 4) return value.split("").map((character) => character.repeat(2)).join("");
  return value;
}

function colorValue(hex: string, opacity?: string) {
  const expanded = expandHex(hex);
  if (!opacity || expanded.length === 8) return `#${expanded}`;
  const alpha = Math.max(0, Math.min(100, Number(opacity))) / 100;
  const red = Number.parseInt(expanded.slice(0, 2), 16);
  const green = Number.parseInt(expanded.slice(2, 4), 16);
  const blue = Number.parseInt(expanded.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export type SourceColorRepairSummary = Readonly<{
  backgrounds: number;
  text: number;
  borders: number;
  gradients: number;
}>;

export function applySourceColorRepairs(root: ParentNode): SourceColorRepairSummary {
  const summary = { backgrounds: 0, text: 0, borders: 0, gradients: 0 };
  root.querySelectorAll<HTMLElement>("[class]").forEach((element) => {
    const gradient: { from?: string; via?: string; to?: string; direction?: string } = {};
    element.classList.forEach((token) => {
      if (directions[token]) gradient.direction = directions[token];
      const match = token.match(arbitraryColor);
      const namedMatch = token.match(namedGradientColor);
      if (!match && !namedMatch) return;
      const [, kind, rawColor, opacity] = match ?? namedMatch ?? [];
      const hex = match ? rawColor : rawColor ? gradientPalette[rawColor.toLowerCase()] : undefined;
      if (!kind || !hex) return;
      const value = colorValue(hex, opacity);
      if (kind === "bg") { element.style.backgroundColor = value; summary.backgrounds += 1; }
      if (kind === "text") { element.style.color = value; summary.text += 1; }
      if (kind === "border") { element.style.borderColor = value; summary.borders += 1; }
      if (kind === "from" || kind === "via" || kind === "to") gradient[kind] = value;
    });
    if (gradient.direction && gradient.from && gradient.to) {
      const stops = gradient.via ? `${gradient.from}, ${gradient.via}, ${gradient.to}` : `${gradient.from}, ${gradient.to}`;
      element.style.backgroundImage = `linear-gradient(${gradient.direction}, ${stops})`;
      summary.gradients += 1;
    }
  });
  return summary;
}
